import { useAtom } from 'jotai';
import { z } from 'zod';

import {
	createLog,
	emailSchema,
	LogSeverity,
	nameSchema,
	phoneSchema,
} from '@ekhodealer/ekho-common';
import { Box, Form } from '@ekhodealer/ekho-common/rev';

import type { ServicesProvider } from '@ekhodealer/ekho-common';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

import { useUpdateBusinessDetails } from '../../api/configuration/business-details/useUpdateBusinessDetails';
import { forceValidateBusinessContactAtom } from '../../atoms/onboardingValidationAtoms';

/**
 * Helper function to get initial form data from ServicesProvider
 */
function getDefaultValues(servicesProvider: ServicesProvider | undefined | null): {
	fullName: string;
	title: string;
	phone: string;
	email: string;
} {
	if (!servicesProvider) {
		return {
			fullName: '',
			title: '',
			phone: '',
			email: '',
		};
	}

	const primaryContact = servicesProvider.primaryContact;

	return {
		fullName: primaryContact?.fullName || '',
		title: primaryContact?.title || '',
		phone: primaryContact?.phone || '',
		email: primaryContact?.email || '',
	};
}

/**
 * Helper function to get Partial<ServicesProvider> from form data
 */
function getServicesProviderFromFormData(
	data: { fullName: string; title: string; phone: string; email: string },
	currentServicesProvider: ServicesProvider,
): Partial<ServicesProvider> {
	return {
		primaryContact: {
			fullName: data.fullName || currentServicesProvider.primaryContact?.fullName || '',
			title: data.title || currentServicesProvider.primaryContact?.title || '',
			phone: data.phone || currentServicesProvider.primaryContact?.phone || '',
			email: data.email || currentServicesProvider.primaryContact?.email || '',
		},
	};
}

const businessContactSchema = z.object({
	fullName: nameSchema,
	title: z.string().min(1, 'Title is required'),
	phone: phoneSchema,
	email: emailSchema,
});

type BusinessContactFormData = z.infer<typeof businessContactSchema>;

export function BusinessContactForm() {
	const { data: servicesProvider } = useCurrentServicesProvider();
	const updateBusinessDetailsMutation = useUpdateBusinessDetails();
	const [forceValidation, setForceValidation] = useAtom(forceValidateBusinessContactAtom);

	// Handle form submit
	const handleSubmit = async (data: BusinessContactFormData): Promise<{ isSuccess: boolean }> => {
		if (!servicesProvider) return { isSuccess: false };

		try {
			const updatedServicesProvider = getServicesProviderFromFormData(data, servicesProvider);
			await updateBusinessDetailsMutation.mutateAsync({
				servicesProvider: updatedServicesProvider,
			});
			setForceValidation(false);
			return { isSuccess: true };
		} catch (error) {
			createLog(
				'config-mgmt',
				`Failed to update business contact: ${
					error instanceof Error ? error.message : String(error)
				}`,
				LogSeverity.ERROR,
			);
			return { isSuccess: false };
		}
	};

	return (
		<Box padding='24' background='secondary' rounding='12'>
			<Form
				name='business-contact'
				schema={businessContactSchema}
				defaultValues={getDefaultValues(servicesProvider)}
				onSubmit={handleSubmit}
				forceValidation={forceValidation}
				title='Contact details'
				titleLevel='subgroup'
				titleWeight='semibold'
				showSaveAndClear>
				<Form.Field.Text
					schemaField='fullName'
					labelText='Full Name'
					required
					size='large'
					placeholder='Enter full name'
				/>
				<Form.Field.Text
					schemaField='title'
					labelText='Title'
					required
					size='large'
					placeholder='Enter title'
				/>
				<Form.Field.Text
					schemaField='phone'
					labelText='Phone'
					required
					size='large'
					type='tel'
					placeholder='Enter phone'
				/>
				<Form.Field.Text
					schemaField='email'
					labelText='Email'
					required
					size='large'
					type='email'
					placeholder='Enter email'
				/>
			</Form>
		</Box>
	);
}

export default BusinessContactForm;
